// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { TruncatedText } from '@/components/truncated-text';
import {
  DeleteRounded,
  EditRounded,
  InsertDriveFileOutlined,
  LinkRounded,
} from '@/constants/hardfin-standard-icons';
import { Attachment } from '@/file/api/types';
import { createViewableURLString } from '@/utilities/create-viewable-url-string';
import { formatFileSize } from '@/utilities/format-file-size';
import { getMimeTypeLabel } from '@/utilities/format-mime-type';
import {
  Box,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

export interface AttachmentInlineListItem {
  attachment: Attachment;
  onDelete: () => void;
  onEdit?: () => void;
}

interface AttachmentInlineListProps {
  items: AttachmentInlineListItem[];
  deleteDisabled: boolean;
}

interface RowProps {
  attachment: Attachment;
  deleteDisabled: boolean;
  onDelete: () => void;
  onEdit?: () => void;
}

function getAttachmentDateLabel(attachment: Attachment) {
  return attachment.datedAt.toLocal().toLocaleString({ dateStyle: 'medium' });
}

function getAttachmentDateTooltip(attachment: Attachment) {
  const dateAction = attachment.type === 'file' ? 'Uploaded' : 'Updated';
  const localDateTime = attachment.datedAt.toLocal().toLocaleString({
    dateStyle: 'long',
    timeStyle: 'long',
  });

  return `${dateAction}: ${localDateTime}`;
}

function getAttachmentMeta(attachment: Attachment) {
  if (attachment.type === 'link') {
    return attachment.name ? attachment.url : null;
  }

  const sizeLabel =
    attachment.size !== undefined ? formatFileSize(attachment.size) : '—';
  const mimeLabel = attachment.mimeType
    ? ` (${getMimeTypeLabel(attachment.mimeType)})`
    : '';

  return `${sizeLabel}${mimeLabel}`;
}

function getAttachmentHref(attachment: Attachment) {
  if (attachment.type === 'file') {
    return attachment.url;
  }

  return createViewableURLString(attachment.url);
}

function AttachmentInlineRow(props: RowProps) {
  const attachment = props.attachment;
  const isFile = attachment.type === 'file';
  const href = getAttachmentHref(attachment);
  const title = attachment.name ?? attachment.url;
  const meta = getAttachmentMeta(attachment);

  return (
    <Box
      sx={{
        minWidth: 0,
        paddingX: 1,
        paddingY: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': {
          borderBottom: 'none',
        },
        '&:hover': {
          backgroundColor: 'action.hover',
          '& .attachment-actions': {
            opacity: 1,
          },
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
        {isFile ? (
          <InsertDriveFileOutlined
            fontSize="small"
            sx={{ color: 'text.secondary', flexShrink: 0 }}
          />
        ) : (
          <LinkRounded
            fontSize="small"
            sx={{ color: 'text.secondary', flexShrink: 0 }}
          />
        )}
        <Box
          component={href ? 'a' : 'div'}
          href={href || undefined}
          target={href ? '_blank' : undefined}
          rel={href ? 'noopener noreferrer' : undefined}
          sx={{
            color: 'inherit',
            flex: 1,
            minWidth: 0,
            textDecoration: 'none',
          }}
        >
          <TruncatedText
            text={title}
            variant="body2"
            sx={{ color: href ? 'primary.main' : 'text.primary' }}
          />
        </Box>
        <Stack
          className="attachment-actions"
          direction="row"
          spacing={0.5}
          sx={{
            opacity: { xs: 1, sm: 0 },
            transition: 'opacity 0.2s ease',
          }}
        >
          {props.onEdit && (
            <IconButton
              size="small"
              onClick={props.onEdit}
              aria-label="Edit link"
            >
              <EditRounded fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={props.onDelete}
            disabled={props.deleteDisabled}
            aria-label={`Delete ${isFile ? 'file' : 'link'}`}
          >
            <DeleteRounded fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        spacing={2}
        sx={{ minWidth: 0, paddingLeft: 4, paddingRight: 1 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {meta && (
            <TruncatedText
              text={meta}
              variant="caption"
              color="text.secondary"
            />
          )}
        </Box>
        <Tooltip title={getAttachmentDateTooltip(attachment)} arrow>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flexShrink: 0 }}
          >
            {getAttachmentDateLabel(attachment)}
          </Typography>
        </Tooltip>
      </Stack>
    </Box>
  );
}

export function AttachmentInlineList(props: AttachmentInlineListProps) {
  return (
    <Stack spacing={0} marginBottom={1} minWidth={0}>
      {props.items.map((item) => (
        <AttachmentInlineRow
          key={`${item.attachment.type}-${item.attachment.id}`}
          attachment={item.attachment}
          deleteDisabled={props.deleteDisabled}
          onDelete={item.onDelete}
          onEdit={item.onEdit}
        />
      ))}
    </Stack>
  );
}

export function AttachmentInlineListSkeleton() {
  return (
    <Stack spacing={1} marginBottom={2}>
      {[0, 1].map((index) => (
        <Stack key={index} direction="row" alignItems="center" spacing={1.5}>
          <Skeleton variant="circular" width={20} height={20} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" height={24} />
            <Skeleton variant="text" width="45%" height={18} />
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}
